import {
  Culture,
  Variety,
  PaginationData,
  SeedInventoryEntry,
  Product,
} from 'constants/interface';
import { InventoryEntryTypeEnum, InventoryTypeEnum } from 'constants/inventory';
import { ProductTypeEnum } from 'constants/product';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateCultureData extends Omit<
  Culture,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {}

export interface GetCultureProductsFilterData extends PaginationData {
  type?: ProductTypeEnum
  isActive?: boolean
}

export interface GetCultureProductsRequestData {
  cultureId: string
  filterData: GetCultureProductsFilterData
}

export interface CreateProductData {
  // type: ProductTypeEnum
  periodFormation?: number
  periodGermination?: number
  periodGrafting?: number
  periodHealing?: number
  loss?: number
  // variety: Variety
  isActive: boolean
}

export interface UpdateProductData extends CreateProductData {
  // id?: string
}

interface VarietyData extends Omit<
Variety,
'id' |
'createdAt' |
'updatedAt' |
'createdBy' |
'updatedBy' |
'culture'
> {
  productStandard?: CreateProductData
  productScion?: CreateProductData
  productStock?: CreateProductData
}

export interface CreateVarietyData {
  cultureId?: string
  data: VarietyData
}

export interface UpdateCultureData {
  id: string
  data: CreateCultureData
}

export interface UpdateVarietyData {
  id: string
  data: VarietyData
}

export interface VarietyProductsData {
  standard: Product,
  scion: Product,
  stock: Product,
}

// SEED ENTRIES

export interface GetSeedEntriesList extends PaginationData {
  type?: InventoryEntryTypeEnum
  inventoryType?: InventoryTypeEnum
  organization?: string
}

export interface ProductApi {
  createCulture(cultureData: CreateCultureData): Promise<Culture>
  updateCulture(cultureData: UpdateCultureData): Promise<Culture>
  getCultureList(filter: PaginationData): Promise<PaginatedResponse<Culture>>
  getCultureProducts(data: GetCultureProductsRequestData): Promise<PaginatedResponse<Product>>

  getVarietyList(filter: PaginationData): Promise<PaginatedResponse<Variety>>
  createVariety(varietyData: CreateVarietyData): Promise<Variety>
  updateVariety(varietyData: UpdateVarietyData): Promise<Variety>
  getVarietyProducts(varietyId: string): Promise<VarietyProductsData>

  getProductList(filter: PaginationData): Promise<PaginatedResponse<Product>>

  getSeedEntriesList(filter: PaginationData): Promise<PaginatedResponse<SeedInventoryEntry>>
}

const productApiBuilder = (): ProductApi => {
  const createCulture = async (cultureData: CreateCultureData): Promise<Culture> => {
    const params: Record<string, unknown> = {
      ...cultureData,
    };
    const result = await requestClient.post('/cultures', params);

    const createdCulture: Culture = result.data as Culture;
    return createdCulture;
  };

  const updateCulture = async (cultureData: UpdateCultureData): Promise<Culture> => {
    const { id: cultureId, data } = cultureData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/cultures/${cultureId}`, params);
    const updatedCulture: Culture = result.data as Culture;
    return updatedCulture;
  };

  const getCultureList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<Culture>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/cultures', params);
    const pagination = getPaginationHeaders(result);
    const cultureList: Culture[] = result.data as Culture[];
    return {
      pagination,
      data: cultureList,
    };
  };

  const getCultureProducts = async (
    requestData: GetCultureProductsRequestData,
  ): Promise<PaginatedResponse<Product>> => {
    const params = buildPaginationParams(requestData.filterData);
    const result = await requestClient.get(`/cultures/${requestData.cultureId}/products`, params);
    const pagination = getPaginationHeaders(result);
    const productList: Product[] = result.data as Product[];
    return {
      pagination,
      data: productList,
    };
  };

  const getVarietyList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<Variety>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/varieties', params);
    const pagination = getPaginationHeaders(result);
    const varietyList: Variety[] = result.data as Variety[];
    return {
      pagination,
      data: varietyList,
    };
  };

  const createVariety = async (
    varietyData: CreateVarietyData,
  ): Promise<Variety> => {
    const { cultureId, data } = varietyData;
    const params: Record<string, unknown> = {
      ...data,
    };
    const result = await requestClient.post(`/cultures/${cultureId}/varieties`, params);

    const createdVariety: Variety = result.data as Variety;
    return createdVariety;
  };

  const updateVariety = async (
    varietyData: UpdateVarietyData,
  ): Promise<Variety> => {
    const { id: varietyId, data } = varietyData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/varieties/${varietyId}`, params);
    const updatedVariety: Variety = result.data as Variety;
    return updatedVariety;
  };

  const getVarietyProducts = async (
    varietyId: string,
  ): Promise<VarietyProductsData> => {
    const result = await requestClient.get(`/varieties/${varietyId}/products`);
    const varietyProducts: {
      standard: Product,
      scion: Product,
      stock: Product,
    } = result.data;
    return varietyProducts;
  };

  const getProductList = async (
    filter: PaginationData,
  ): Promise<PaginatedResponse<Product>> => {
    const params = buildPaginationParams(filter);
    const result = await requestClient.get('/products', params);
    const pagination = getPaginationHeaders(result);
    const productList: Product[] = result.data;
    return {
      pagination,
      data: productList,
    };
  };

  // SEED ENTRIES

  const getSeedEntriesList = async (
    filterData: GetSeedEntriesList,
  ): Promise<PaginatedResponse<SeedInventoryEntry>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/varieties/entries', params);
    const pagination = getPaginationHeaders(result);
    const seedEntriesList: SeedInventoryEntry[] = result.data as SeedInventoryEntry[];
    return {
      pagination,
      data: seedEntriesList,
    };
  };

  return {
    createCulture,
    updateCulture,
    getCultureList,
    getCultureProducts,

    getVarietyList,
    createVariety,
    updateVariety,
    getVarietyProducts,

    getProductList,

    getSeedEntriesList,
  };
};

export const productApi = productApiBuilder();
